import {
    BasePathMapping, Cors,
    DomainName,
    IResource,
    IRestApi,
    LambdaIntegration,
    MethodOptions,
    RestApi, UsagePlan,
} from 'aws-cdk-lib/aws-apigateway'
import {Optional} from '@othree.io/optional'
import {IFunction} from 'aws-cdk-lib/aws-lambda'
import {RestApiProps} from 'aws-cdk-lib/aws-apigateway'
import {Stack} from 'aws-cdk-lib'
import {compose, ComposerFn} from './utils/compose'
import {addApiStageToUsagePlan} from './usage-plan'

export type RestApiDomain = Readonly<{
    domain: string
    basePath: string
}>

export type RestApiInput = Readonly<{
    apiName: string
}>

export type RestApiConfiguration = Partial<RestApiProps> & Readonly<{
    domain?: RestApiDomain
    usagePlanId?: string
}>

/** Returns a default REST API configuration with CORS allowing all origins and GET requests. */
export const withDefaultApiConfiguration = (): RestApiConfiguration => {
    return {
        defaultCorsPreflightOptions: {
            allowHeaders: Cors.DEFAULT_HEADERS,
            allowMethods: ['GET'],
            allowOrigins: Cors.ALL_ORIGINS,
        }
    }
}

/**
 * Creates a REST API Gateway with optional custom domain mapping and usage plan attachment.
 */
export const withRestApi = (deps: {
    defaultConfiguration: RestApiConfiguration
    stack: Stack
}) =>
    (input: RestApiInput, configuration?: Partial<RestApiProps>): RestApi => {
        const restApi = new RestApi(deps.stack, input.apiName, {
            restApiName: input.apiName,
            ...deps.defaultConfiguration,
            ...Optional(configuration!).orElse({}),
        })

        if (deps.defaultConfiguration.domain) {
            const domain = DomainName.fromDomainNameAttributes(deps.stack, `${input.apiName}-Domain`, {
                domainName: deps.defaultConfiguration.domain.domain,
            } as any)

            new BasePathMapping(deps.stack, `${input.apiName}-PathMapping`, {
                basePath: deps.defaultConfiguration.domain.basePath,
                domainName: domain,
                restApi: restApi,
            })
        }

        Optional(deps.defaultConfiguration.usagePlanId)
            .map(usagePlanId => {
                return addApiStageToUsagePlan(deps.stack, `${input.apiName}-UsagePlanConstruct`, {
                    ApiId: restApi.restApiId,
                    UsagePlanId: usagePlanId,
                    Stage: restApi.deploymentStage.stageName
                })
            })

        return restApi
    }

type NewResource = Readonly<{
    path: string
    newResource?: IResource
    resources: {
        [key: string]: IResource
    }
}>

export type HttpVerb = 'GET' | 'DELETE' | 'POST' | 'PUT'

/** Enum-like constant for HTTP verb values. */
export const HttpVerb = {
    GET: 'GET' as HttpVerb,
    DELETE: 'DELETE' as HttpVerb,
    POST: 'POST' as HttpVerb,
    PUT: 'PUT' as HttpVerb,
} as const

export type RestConfiguration = Readonly<{
    verb: HttpVerb
    fn: IFunction
    props?: Partial<MethodOptions>
}>

/**
 * Adds a Lambda-backed resource and method to a composed REST API.
 * Nested paths (e.g. `/users/{id}/orders`) are created incrementally and cached to avoid duplicates.
 */
export const withResource = (path: string, configuration: RestConfiguration) =>
    (api: ComposedRestApi): ComposedRestApi => {
        const addResource = (resource: string, fullPath: string, parent: IResource, api: ComposedRestApi): IResource => {
            const maybeResource = Optional(api.resources[fullPath])
            if (maybeResource.isEmpty) {
                return parent.addResource(resource)
            }
            return maybeResource.get()
        }

        const resources = path.startsWith('/')
            ? ['/', ...path.split('/').filter(Boolean)]
            : path.split('/')

        const newResource: NewResource = resources.reduce((fullPath: any, resource: string, index: number): NewResource => {
            const parent = index === 0 ? api.api.root : fullPath.resources[fullPath.path]
            const pathSeparator = fullPath.path.endsWith('/') ? '' : '/'
            const path = index === 0 ? resource : `${fullPath.path}${pathSeparator}${resource}`
            const newResource = resource === '/' ? api.api.root : addResource(resource, path, parent, api)
            return {
                path: path,
                newResource: newResource,
                resources: {
                    ...fullPath.resources,
                    [path]: newResource,
                },
            }
        }, {
            path: '',
            newResource: undefined,
            resources: {},
        } as NewResource)

        const lambdaIntegration = new LambdaIntegration(configuration.fn)
        newResource.newResource!.addMethod(configuration.verb, lambdaIntegration, configuration.props)

        return {
            resources: {
                ...api.resources,
                ...newResource.resources,
            },
            api: api.api,
        }
    }

/** A REST API paired with a resource cache, threaded through composition. */
export type ComposedRestApi = Readonly<{
    api: IRestApi
    resources: {
        [key: string]: IResource
    }
}>

/** Composes a REST API by piping it through a sequence of `withResource` calls. */
export const composeRestApi = (api: IRestApi, ...args: Array<ComposerFn<ComposedRestApi>>): ComposedRestApi => {
    const composedRestApi: ComposedRestApi = {
        api: api,
        resources: {},
    }

    return compose(composedRestApi, ...args)
}
